// ============================================================================
// JARVISTRAVEL - API CLIENT
// Base fetch wrapper with Clerk JWT auth
// ============================================================================

import { reportBackendReachable, reportBackendUnreachable } from './backendStatus';
import { asyncDelay } from '@/app/utils';
import { userMessage } from './failure';
import { completion_ceiling_ms, write_margin_ms } from './concierge-answer-ceiling.json';

// Clerk singleton injected by ClerkProvider
interface ClerkWindow {
  Clerk?: {
    /** clerk-js sets this once it has finished hydrating. */
    loaded?: boolean;
    session?: {
      /** Stable for the life of one signed-in session, across token refreshes.
       *  Used to tell "this session was already announced dead" from "a new
       *  session died" (JAR-914). */
      id?: string;
      /** `skipCache` forces a fresh JWT rather than the cached one. */
      getToken(opts?: { skipCache?: boolean }): Promise<string | null>;
    };
  };
}
declare const window: Window & typeof globalThis & ClerkWindow;

// Production is not a safe default for an unset variable. A fresh clone with no
// .env pointed straight at the live API, so a developer's first `npm run dev`
// read and wrote real customer data believing it was local (JAR-355, low).
// Dev falls back to localhost; a production build with no VITE_API_URL is a
// misconfiguration worth failing on rather than guessing about.
// Dev falls back to localhost so a fresh clone with no .env talks to a local
// backend rather than to production — the state a first `npm run dev` lands in,
// and the original finding.
//
// A production build cannot get here unset: vite.config.ts refuses to build one
// (see requireApiBase). This resolves to an empty string rather than throwing,
// because a module-scope throw fires before React mounts — no ErrorBoundary, no
// demo mode, just a white screen for a config mistake (review H1). An empty
// base makes requests fail as requests, which the app already handles.
const API_BASE = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8080/v1' : '');

/** An address on our API for something the browser fetches by URL rather than
 *  through `request`, such as a stay's photo in an <img> (JAR-2240). */
export const apiUrl = (path: string): string => `${API_BASE}${path}`;
const USER_KEY = 'jarvis_user';

/** How much of an unparseable body to keep for diagnosis. Never user-facing. */
const PARSE_ERROR_EXCERPT = 500;

// A request with no answer in this window is treated as unreachable, so a
// hung connection surfaces the banner instead of spinning forever (JAR-314).
const REQUEST_TIMEOUT_MS = 30_000;

// A Jarvis message waits for the model. Core allows it ai.CompletionCeiling
// plus its handler's write margin, and cancels the answer when the request
// goes away, so the 30s above lost every answer that took longer (JAR-1966).
// The message post alone waits that long, plus slack for the network. Core's
// numbers live in another repo: concierge-answer-ceiling.json copies the ones
// core publishes, and CI fails while the copy differs from them (JAR-2022).
const CORE_ANSWER_CEILING_MS = completion_ceiling_ms;
const CORE_WRITE_MARGIN_MS = write_margin_ms;
const NETWORK_SLACK_MS = 30_000;
const MODEL_ANSWER_TIMEOUT_MS = CORE_ANSWER_CEILING_MS + CORE_WRITE_MARGIN_MS + NETWORK_SLACK_MS;
// The Jarvis message route, which the quiet refusal below also reads.
const JARVIS_MESSAGE_ROUTE = /^\/concierge\/conversations\/[^/]+\/messages$/;
// Every route that waits for the model gets the long wait above: a Jarvis
// message, the planner's brief and a day Jarvis plans (JAR-2280).
const MODEL_ROUTES = [JARVIS_MESSAGE_ROUTE, /^\/concierge\/trip-brief$/, /^\/concierge\/rebuild-day$/];

// The Plaid public-token exchange runs two Plaid calls inside the one request
// (create the item, then the first accounts read), so it routinely takes longer
// than a plain write. At the 30s ceiling above it aborted a link core was about
// to finish, and the panel reset to disconnected while the connection actually
// existed server-side (JAR-2306). Give it a longer wait, the way the model
// routes get one — bounded by Plaid rather than a generation, so shorter.
const PLAID_EXCHANGE_TIMEOUT_MS = 60_000;
const PLAID_EXCHANGE_ROUTE = /^\/trips\/[^/]+\/financial\/plaid\/exchange-public-token$/;

// A good token for an account that is not provisioned YET (JAR-1706).
//
// Core answers 403 ACCOUNT_NOT_PROVISIONED when a token verifies but its subject
// has no local user row (core#810). The usual reason is a race, not a broken
// account: Clerk's user.created reaches core asynchronously through Svix while the
// person already holds a session, so a first request can beat its own row by a
// few seconds. Waiting and asking again heals that without anyone seeing it.
//
// READS ONLY (#446 gate, Fury) — and this one stays reads-only even though the
// post-401 retry below now refreshes writes too (JAR-2268). The two refusals are
// not the same: a 401 is a credential core rejects in middleware before any
// handler runs, so refreshing the token and replaying is safe; this 404 can mean
// the write's own target does not exist yet, which a token refresh does not fix.
// The realistic race is the first reads after sign-in anyway; a write in that
// window gets core's own "try again in a moment", and the person can.
//
// Bounded, because the other reason is permanent — an address collision the
// webhook can never resolve (JAR-1705). After the last wait, the refusal is
// returned as usual, carrying core's own message.
const PROVISIONING_RETRY_DELAYS_MS = [1_000, 2_000, 4_000];

// Shorter timeout for the explicit reachability probe (M2 / JAR-314): when
// we're specifically checking if the host answers, 30s is too punishing on
// a flaky connection — surface the result in 10s so the "Try again" button
// and periodic auto-probe feel responsive.
const PROBE_TIMEOUT_MS = 10_000;

// ---------------------------------------------------------------------------
// Clerk Token — read the active session's JWT from the Clerk singleton.
// ---------------------------------------------------------------------------

// clerk-js is injected asynchronously by ClerkProvider, but routes render
// immediately — so a hard refresh on an authenticated route could fire its
// first request before `window.Clerk` existed. That request went out with no
// Authorization header, earned a 401, and the 401 handler read it as a dead
// session and signed the user out. Refreshing the page logged you out (JAR-448).
//
// Bounded deliberately: if Clerk genuinely never arrives we proceed without a
// token rather than hanging the request forever. The caller then knows no token
// was sent, which is what keeps the resulting 401 from being mistaken for an
// expired session.
const CLERK_READY_TIMEOUT_MS = 3_000;
const CLERK_POLL_MS = 25;

// Ready means clerk-js has finished hydrating — NOT that someone is signed in.
// Waiting for a session would stall every request by the full timeout for a
// signed-out visitor, who legitimately has none.
const clerkReady = () => Boolean(window.Clerk?.loaded || window.Clerk?.session);

async function waitForClerk(): Promise<void> {
  if (clerkReady()) return;
  const deadline = Date.now() + CLERK_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, CLERK_POLL_MS));
    if (clerkReady()) return;
  }
}

/**
 * Get the current Clerk session JWT for API auth headers.
 *
 * `forceRefresh` bypasses Clerk's cache — used once after a 401, since the
 * most likely cause is a token that expired between being minted and arriving.
 */
async function getTokenFromClerk(forceRefresh = false): Promise<string | null> {
  try {
    await waitForClerk();
    if (window.Clerk?.session) {
      return (await window.Clerk.session.getToken(forceRefresh ? { skipCache: true } : undefined)) ?? null;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Stored User — still needed for demo mode.
// ---------------------------------------------------------------------------

function readAuthItem(key: string): string | null {
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}

function writeAuthItem(key: string, value: string, remember: boolean): void {
  const [primary, secondary] = remember
    ? [localStorage, sessionStorage]
    : [sessionStorage, localStorage];
  primary.setItem(key, value);
  secondary.removeItem(key);
}

function removeAuthItem(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

export function getStoredUser<T>(): T | null {
  try {
    const raw = readAuthItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredUser<T>(user: T, remember = true): void {
  writeAuthItem(USER_KEY, JSON.stringify(user), remember);
}

/** Whether the current dead session has already been announced.
 *
 *  `auth:expired` is a broadcast about the SESSION, not about a request, so it
 *  is worth exactly one dispatch per expiry. It used to fire per 401, which was
 *  indistinguishable while writes were sequential — the first 401 ended the
 *  session and nothing else was in flight. JAR-828 made saveBudgetAllocations
 *  issue its category writes concurrently, so one expired session during a
 *  budget save now produces seven 401s, seven clearStoredUser() calls and seven
 *  handler runs in App.tsx.
 *
 *  Idempotent today. It stops being idempotent the moment the handler grows a
 *  toast, a redirect, or an analytics event — and more concurrency is the
 *  direction of travel, so the fix belongs here rather than in each handler
 *  (JAR-914).
 */
/*  Which credential we have already announced dead, or null.
 *
 *  An identity rather than a boolean, and that removes the reset step instead
 *  of making it cleverer (web-app#218 review). A boolean has to be re-armed by
 *  something, and the first version re-armed on "a token-carrying response that
 *  was not refused" — which keys on a response's TIMING, not on which
 *  credential it carried. Two races followed, and both are the bug this exists
 *  to fix, wearing a different hat:
 *
 *    - a 200 the server processed BEFORE the session died but that ARRIVES
 *      after the first 401 re-armed the flag mid-batch, so a later 401 from
 *      the same dead batch announced a second time.
 *    - a stale write's 401, carrying the OLD token, landing after the user has
 *      already signed back in. The flag was clear, so it announced: signing the
 *      user out of a session that was working, and then swallowing the first
 *      genuine expiry of that new session.
 *
 *  Comparing identity answers both without a reset. The same credential is
 *  never announced twice however its responses interleave, and a different one
 *  is always news. */
let announcedForCredential: string | null = null;

/** Test seam: forget which credential was announced, so each case starts clean.
 *
 *  Module state outlives a single test. Without this, the first case to
 *  announce silently suppresses the dispatch for every case after it — which
 *  looks exactly like the bug this exists to fix. */
export function resetSessionExpiryState(): void {
  announcedForCredential = null;
}

export function clearStoredUser(): void {
  removeAuthItem(USER_KEY);
}

// ---------------------------------------------------------------------------
// Demo Session Management
// ---------------------------------------------------------------------------

const DEMO_KEY = 'jarvis_demo_session';

export function setDemoSession(): void {
  localStorage.setItem(DEMO_KEY, '1');
}

export function clearDemoSession(): void {
  localStorage.removeItem(DEMO_KEY);
}

export function isDemoSession(): boolean {
  return localStorage.getItem(DEMO_KEY) === '1';
}

// ---------------------------------------------------------------------------
// API Response Types
// ---------------------------------------------------------------------------

export interface ApiError {
  error: string;
  message: string;
  code?: string;
}

export interface ApiResult<T> {
  data: T | null;
  error: ApiRequestError | null;
  status: number;
}

/**
 * A failed API call. `code` holds the machine-readable error type so callers
 * can tell transport failures apart from real backend answers.
 *
 * All error returns from `request()` construct `ApiRequestError` instances, so
 * callers can use `error instanceof ApiRequestError` and `error.isNetworkError`
 * instead of string-matching on the error type (JAR-426).
 *
 * `.error` is a backward-compat getter that returns `this.code` — callers that
 * previously read `ApiError.error` (the code string) can keep doing so.
 *
 * `.detailCode` carries an optional secondary code (e.g. `'TIMEOUT'`) when the
 * error path sets one.
 */
/** Raised on window, with core's sentence as its detail, when a write is
 *  refused because its Trip Pass trip has ended (JAR-1860). */
export const TRIP_LOCKED_EVENT = 'trip:locked';

// Writes whose refusal raises no toast. Jarvis answers a message itself
// (useConcierge). A bank sync and a flight status pull run when a panel opens,
// so nobody was changing anything, and a toast would come back on every visit.
const QUIET_WHEN_LOCKED = [
  JARVIS_MESSAGE_ROUTE,
  /^\/trips\/[^/]+\/financial\/plaid\/transactions\/sync$/,
  /^\/trips\/[^/]+\/flights\/refresh$/,
];

// Reads a screen does without, which core answers with a 502 or 503 when the
// provider behind them is down. That, or their own slow answer, says nothing
// about the rest of core, and taking the whole app offline over one of them hid
// a signed-in person's trips (JAR-2266). A network error still counts: it is
// the device, not the provider.
const OPTIONAL_ROUTES = [
  /^\/trips\/[^/]+\/weather(\/refresh)?$/,
  /^\/location\/geosearch$/,
];

/** A refusal from a Trip Pass trip that has ended: its planning (423
 *  TRIP_PLANNING_LOCKED) or, 7 business days later, its expenses (423
 *  TRIP_EXPENSES_LOCKED). Its message is a sentence for the person (JAR-1738). */
export function isTripLocked(e: unknown): e is ApiRequestError {
  // Read by shape, not by class. `failure.ts` reads an API error the same way
  // and says why: an error that crossed a module boundary, or came from a
  // second copy of this module, is still that refusal, and an `instanceof`
  // that quietly answers false would drop it to the generic failure path.
  if (!e || typeof e !== 'object') return false;
  const { status, detailCode } = e as { status?: number; detailCode?: string };
  return status === 423 && (detailCode === 'TRIP_PLANNING_LOCKED' || detailCode === 'TRIP_EXPENSES_LOCKED');
}

/** What the client raises, and what a screen says, when core's own sentence
 *  for a lock refusal is unusable. One constant, so both say the same words and
 *  the toast store keeps a single copy (JAR-1864). */
export const TRIP_LOCKED_FALLBACK = 'This trip has ended, so it can’t be changed.';

/**
 * The line a screen shows when one of its writes failed (JAR-1864). A refusal
 * from a Trip Pass trip that has ended is core's sentence: the words this
 * client has already raised as a toast, so the toast store shows one copy, and
 * never "try again", which cannot work on a trip that has ended. Anything else
 * is the screen's own line, unchanged. A wrapper that keeps the client's error
 * as its `cause`, as BankSyncError does, is seen through.
 */
export function writeFailureMessage(error: unknown, own: string): string {
  const cause = error && typeof error === 'object' ? (error as { cause?: unknown }).cause : undefined;
  const locked = isTripLocked(error) ? error : isTripLocked(cause) ? cause : undefined;
  return locked ? userMessage(locked, TRIP_LOCKED_FALLBACK) : own;
}

/**
 * `Retry-After` as milliseconds: delta-seconds or an HTTP-date, whichever the
 * server sent, or undefined. Guarded for a response with no `headers` (test
 * doubles), where it is simply absent (JAR-2419).
 */
function parseRetryAfterMs(response: Response): number | undefined {
  const raw = response?.headers?.get?.('retry-after');
  if (!raw) return undefined;
  const secs = Number(raw);
  if (Number.isFinite(secs)) return Math.max(0, secs * 1000);
  const when = Date.parse(raw);
  return Number.isNaN(when) ? undefined : Math.max(0, when - Date.now());
}

export class ApiRequestError extends Error {
  readonly code: string;
  readonly detailCode?: string;
  /**
   * A bounded excerpt of a body we could not parse. Diagnostic only — it is
   * whatever a proxy, CDN or error page happened to return, so it is never
   * shown to a user. See userMessage() in ./failure.
   */
  readonly rawBody?: string;
  /** The HTTP status of the answer, when there was one. */
  readonly status?: number;
  /**
   * Milliseconds to wait before retrying, parsed from a 429's `Retry-After`
   * header when present (JAR-2419). Undefined when the server sent none. A
   * passive field; acting on it client-wide is JAR-3321, and readTripDetail is
   * the one caller that retries on it today.
   */
  readonly retryAfterMs?: number;

  constructor(code: string, message: string, detailCode?: string, status?: number, retryAfterMs?: number) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.detailCode = detailCode;
    this.status = status;
    this.retryAfterMs = retryAfterMs;
  }

  /** @deprecated Use `.code` directly — kept for backward compat with ApiError shape. */
  get error(): string {
    return this.code;
  }

  /** True when the request never got an answer (offline, DNS, CORS). */
  get isNetworkError(): boolean {
    return this.code === 'NetworkError';
  }
}

// ---------------------------------------------------------------------------
// Fetch Wrapper
// ---------------------------------------------------------------------------

/**
 * Core fetch wrapper. Gets a Clerk JWT for auth, handles 401 (session expiry
 * via custom event), 204 No Content, and network error normalization.
 *
 * Clerk manages session refresh automatically — no manual refresh logic.
 *
 * @param method - HTTP method
 * @param path - API path (appended to API_BASE)
 * @param body - Optional request body (serialized to JSON)
 * @param auth - Whether to include Authorization header (default: true)
 */
async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  auth: boolean = true,
  // Headers this one call adds, such as the Idempotency-Key on a trip create
  // (JAR-1762). The retries below pass them on: they retry reads only today, but
  // a write retried without its key would be a second create.
  extraHeaders: Record<string, string> = {},
  // Internal: set on the single post-401 retry so it cannot recurse.
  isRetry = false,
  // Internal: how many not-yet-provisioned waits this request has already made.
  provisioningAttempt = 0,
  // The caller's own way to end the wait. A trip switch abandons the old trip's
  // Jarvis answer with it, so core stops working on it (JAR-1976).
  signal?: AbortSignal,
): Promise<ApiResult<T>> {
  const url = `${API_BASE}${path}`;

  // Demo sessions never touch the real API (JAR-292): writes return fake
  // success (nothing to persist), reads return a synthetic miss so every
  // consumer takes its sample-data fallback without network churn.
  //
  // Checked before the token is fetched — a demo session has no Clerk session
  // to wait for, and paying the readiness timeout to build a header we then
  // throw away would stall every demo call.
  if (isDemoSession()) {
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      return { data: {} as T, error: null, status: 200 };
    }
    return {
      data: null,
      error: new ApiRequestError('DemoSession', 'Demo session: using sample data', 'DEMO_MODE'),
      status: 0,
    };
  }

  // Multipart bodies (FormData) must NOT carry a manual Content-Type — the
  // browser sets it with the required boundary. Only JSON bodies get the
  // explicit header. This is what lets file uploads (e.g. receipt scan, JAR-449)
  // go through the shared client instead of bypassing it.
  const isFormData = body instanceof FormData;
  const headers: Record<string, string> = { ...(isFormData ? {} : { 'Content-Type': 'application/json' }), ...extraHeaders };

  // Whether a token actually went out. A 401 on a request that carried no
  // credential says nothing about the validity of the session — it is the
  // difference between "your session died" and "we asked without asking
  // properly" (JAR-448).
  let sentToken = false;
  // WHICH credential went out, captured here rather than read back when the
  // response lands. By then the user may have signed in again, and the question
  // a 401 answers is "was the credential I SENT refused", not "is there a
  // session now".
  //
  // The session id in preference to the token because it is stable across
  // Clerk's token refreshes, so one dying session announces once even if its
  // JWT rotated mid-batch.
  //
  // The token is the fallback for any Clerk build that does not expose an id.
  // It is a WORSE key, not an equivalent one (JAR-1004): distinct across
  // sign-ins but not stable within one, so a rotation re-announces — pinned in
  // client.test.ts at one dispatch per JWT issued, which is the JAR-914 bug
  // itself. What holds it off real users is clerk-js's token cache handing a
  // concurrent batch one JWT, and real clerk-js exposing session.id anyway.
  //
  // It stays the fallback because every alternative fails the other way.
  // Anything stable across sign-ins — a sentinel, user.id — SWALLOWS a later
  // expiry: there is no production reset (resetSessionExpiryState is a test
  // seam), so the suppression lasts the life of the tab. That is race two
  // above, unbounded and silent. A double announce is bounded and loud.
  let sentCredential: string | null = null;

  if (auth) {
    const token = await getTokenFromClerk(isRetry);
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      sentToken = true;
      sentCredential = window.Clerk?.session?.id ?? token;
    }
  }

  // Abort a request that never answers so the UI can fall back instead of
  // hanging on a pending promise.
  const controller = new AbortController();
  const waitsForTheModel = method === 'POST' && MODEL_ROUTES.some((route) => route.test(path));
  const slowExchange = method === 'POST' && PLAID_EXCHANGE_ROUTE.test(path);
  const optional = OPTIONAL_ROUTES.some((route) => route.test(path));
  const requestTimeoutMs = waitsForTheModel ? MODEL_ANSWER_TIMEOUT_MS : slowExchange ? PLAID_EXCHANGE_TIMEOUT_MS : REQUEST_TIMEOUT_MS;
  const timeoutId = setTimeout(() => controller.abort(), requestTimeoutMs);
  const abandon = () => controller.abort();
  if (signal?.aborted) controller.abort();
  else signal?.addEventListener('abort', abandon, { once: true });

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: isFormData ? (body as FormData) : body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });

    const status = response.status;

    // A Jarvis answer that runs out of time comes back as a 504, from core at
    // its answer ceiling or from nginx past it. The server answered and the
    // model was slow, so it is the same timeout as the wait above running out,
    // not an outage (JAR-1966).
    const answerTimedOut = waitsForTheModel && status === 504;

    // The backend answered. Anything < 500 means it's reachable and serving
    // (a 401/404 is a real answer, not an outage). A 5xx usually means it's up
    // but failing, which for the user is the same "can't serve you" state — the
    // EXCEPTION being a 5xx carrying one of our own error codes, which proves
    // core answered and is relaying an upstream's refusal rather than being down
    // (a Plaid refusal comes back 502 PLAID_API_ERROR; JAR-2300). That takes the
    // parsed body, so a 5xx's reachability verdict is deferred to the error
    // handling below; every other status is settled here.
    const serverFailing = status >= 500 && !answerTimedOut;
    if (!serverFailing) reportBackendReachable();

    if (answerTimedOut) {
      // Not 'NetworkError': the server answered, so isNetworkError stays false.
      return { data: null, error: new ApiRequestError('AnswerTimeout', 'The server took too long to respond', 'TIMEOUT', status), status };
    }

    if (status === 401) {
      // A 401 for a request that carried no credential is not evidence of an
      // expired session. Ending the session here is what turned a cold-load
      // race, and any call on the public share route, into a spurious sign-out.
      if (!sentToken) {
        return { data: null, error: new ApiRequestError('Unauthorized', 'Not authorized', 'NO_CREDENTIAL', status), status };
      }

      // A token that expired between minting and arriving is routine, not a
      // failure. Refresh once and retry — for writes too, not reads only
      // (JAR-2268). Core decides a 401 in its auth middleware, before any handler
      // runs (auth.go), so a 401'd write never reached its handler and cannot
      // have applied; a create still carries its Idempotency-Key on the retry, so
      // even a replay would dedupe. Retrying reads only was what let one
      // token that expired in flight fall through to the purge below and wipe a
      // still-signed-in user's browser-kept data.
      if (!isRetry) {
        return request<T>(method, path, body, auth, extraHeaders, true, provisioningAttempt, signal);
      }

      // Once per CREDENTIAL. EVERY caller still gets its own SESSION_EXPIRED
      // error below — only the broadcast is deduped, because a concurrent batch
      // of writes is one dead session, not seven.
      //
      // Check-then-set with no await between them, so a batch resolving in the
      // same tick cannot interleave through the gap.
      if (announcedForCredential !== sentCredential) {
        announcedForCredential = sentCredential;
        clearStoredUser();
        window.dispatchEvent(new CustomEvent('auth:expired'));
      }
      return { data: null, error: new ApiRequestError('Unauthorized', 'Session expired', 'SESSION_EXPIRED', status), status };
    }

    // Handle 204 No Content (success, no body)
    if (status === 204) {
      return { data: null as unknown as T, error: null, status };
    }

    const text = await response.text();
    let parsed: T | ApiError;

    try {
      parsed = JSON.parse(text);
    } catch {
      // The body is evidence, not a message. Putting it in `.message` meant a
      // CDN's HTML 502 page reached the user verbatim through any handler that
      // toasts `e.message` — and two of them do (JAR-355 A3/A4). Keep a bounded
      // excerpt for the log, and let userMessage() decide what a person sees.
      const error = new ApiRequestError('ParseError', 'The server sent a response we could not read.', undefined, status);
      // Non-enumerable on purpose. As an own enumerable property this rode along
      // in any `JSON.stringify(error)` and in the Sentry capture that
      // observability.ts has stubbed out — so an excerpt of whatever a proxy
      // returned would have shipped off-device the day either was switched on
      // (review M2). It is read explicitly by reportApiFailure, and by nothing
      // that walks the object.
      Object.defineProperty(error, 'rawBody', {
        value: text.slice(0, PARSE_ERROR_EXCERPT),
        enumerable: false,
        writable: false,
      });
      // A 5xx we could not even parse is a proxy's or CDN's error page: core did
      // not answer, so this IS the outage the banner is for (JAR-2300).
      if (serverFailing && !optional) reportBackendUnreachable();
      return { data: null, error, status };
    }

    if (!response.ok) {
      // Two error shapes reach this line, and only one used to be read.
      //
      // Most of trip-core writes the flat {error, message, code} this parser
      // expects. Budgeting and plaid write a NESTED {error: {message, code}}
      // instead. Against those, `errBody.error` was the object, `.message` and
      // `.code` were undefined, and every refusal became
      // ApiRequestError('[object Object]', 'Request failed') — which is why
      // BELOW_COMMITTED, the entire point of the committed-floor message, could
      // only ever fire in demo mode. The server was refusing correctly; the
      // client was throwing the reason away.
      //
      // Read both here rather than in each caller: this is the one place every
      // response passes through, and a caller that has to know which handler it
      // is talking to is a caller that will get it wrong.
      const errBody = parsed as ApiError | { error?: { message?: string; code?: string } };
      const nested = errBody && typeof errBody.error === 'object' && errBody.error !== null
        ? errBody.error as { message?: string; code?: string }
        : null;
      const flat = errBody as ApiError;
      const detailCode = nested ? nested.code : flat.code;
      const message = (nested ? nested.message : flat.message) || 'Request failed';
      // `code` is the coarse kind for the flat shape; the nested shape has no
      // separate kind, so its detail code stands in.
      const kind = (nested ? nested.code : flat.error) || 'UnknownError';
      if (
        status === 403 &&
        detailCode === 'ACCOUNT_NOT_PROVISIONED' &&
        method === 'GET' &&
        provisioningAttempt < PROVISIONING_RETRY_DELAYS_MS.length
      ) {
        await asyncDelay(PROVISIONING_RETRY_DELAYS_MS[provisioningAttempt]);
        return request<T>(method, path, body, auth, extraHeaders, isRetry, provisioningAttempt + 1, signal);
      }
      const retryAfterMs = status === 429 ? parseRetryAfterMs(response) : undefined;
      const error = new ApiRequestError(kind, message, detailCode, status, retryAfterMs);
      // With the body parsed, settle the deferred 5xx reachability verdict
      // (JAR-2300). Core answered with its own error envelope, so a Plaid
      // refusal (502 PLAID_API_ERROR) is core working and relaying Plaid's "no",
      // not an outage — leave the banner down (and clear a stale one). Any other
      // 5xx is core itself failing, so raise it.
      if (serverFailing && !optional) {
        if (detailCode === 'PLAID_API_ERROR') reportBackendReachable();
        else reportBackendUnreachable();
      }
      // A Trip Pass trip that has ended refuses planning writes, and later its
      // expense writes, with a sentence for the person (JAR-1738). Every screen
      // hears it the same way: App shows it as a toast, however the screen
      // reports its own failure. Jarvis's chat says it as Jarvis instead.
      // Writes only: core refuses no read this way, and a read that did would
      // raise the toast again on every visit to the trip.
      if (isTripLocked(error) && method !== 'GET' && !QUIET_WHEN_LOCKED.some((quiet) => quiet.test(path))) {
        // Through userMessage, as every other backend sentence reaches a
        // person: bounded, and never a proxy's error page.
        window.dispatchEvent(new CustomEvent<string>(TRIP_LOCKED_EVENT, {
          detail: userMessage(error, TRIP_LOCKED_FALLBACK),
        }));
      }
      return { data: null, error, status };
    }

    return { data: parsed as T, error: null, status };
  } catch (err) {
    // Never reached the backend (offline, DNS, CORS) or it timed out
    // (AbortError). Both surface the "unavailable" banner (JAR-314), except a
    // Jarvis answer that ran out its long wait: a slow model is not an outage
    // (JAR-1966).
    // The caller ended it: nothing failed, and nobody is waiting for an answer.
    if (signal?.aborted) {
      return { data: null, error: new ApiRequestError('Aborted', 'The request was abandoned', 'ABORTED'), status: 0 };
    }
    const timedOut = err instanceof DOMException && err.name === 'AbortError';
    if (!(timedOut && (waitsForTheModel || optional))) reportBackendUnreachable();
    const msg = timedOut ? 'The server took too long to respond' : err instanceof Error ? err.message : 'Network request failed';
    return {
      data: null,
      error: timedOut
        ? new ApiRequestError('NetworkError', msg, 'TIMEOUT')
        : new ApiRequestError('NetworkError', msg),
      status: 0,
    };
  } finally {
    clearTimeout(timeoutId);
    signal?.removeEventListener('abort', abandon);
  }
}

/**
 * Manually re-test backend reachability (the banner's "Try again"). Any HTTP
 * answer — even a 404 — means the host is reachable, so we clear the offline
 * state; only a network throw keeps it. Deliberately unauthenticated and
 * body-less so it works regardless of session state.
 */
export async function probeBackend(): Promise<boolean> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    await fetch(API_BASE, { method: 'HEAD', signal: controller.signal });
    reportBackendReachable();
    return true;
  } catch {
    reportBackendUnreachable();
    return false;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export const api = {
  get: <T>(path: string, auth = true, signal?: AbortSignal) => request<T>('GET', path, undefined, auth, {}, false, 0, signal),
  post: <T>(path: string, body?: unknown, auth = true, headers?: Record<string, string>, signal?: AbortSignal) =>
    request<T>('POST', path, body, auth, headers, false, 0, signal),
  put: <T>(path: string, body?: unknown, auth = true) => request<T>('PUT', path, body, auth),
  patch: <T>(path: string, body?: unknown, auth = true) => request<T>('PATCH', path, body, auth),
  del: <T>(path: string, body?: unknown, auth = true) => request<T>('DELETE', path, body, auth),
};
